/* ═══════════════════════════════════════════════════════════════
   formaDraftModel — the PURE decisions behind the master forma's shared
   draft (Sync appendix items 7 + 8). No React, no socket: Yjs in, values
   out, so every rule here is unit-tested directly (formaDraftModel.test.ts).
   `useMasterFormaDraft` is the thin React shell around these.
   ═══════════════════════════════════════════════════════════════ */

import type * as Y from 'yjs';
import type { StandardConfig } from '../../shared/types';
import { pushEditHistory } from '../../shared/editHistory';
import { applyFormaDiff, markDirty, readForma } from './formaCollab';
import {
  CURSOR_SLOTS,
  draftReloadPlan,
  type CollabPeer,
  type CollabStatus,
  type LiveBlock,
} from '../spec-studio/collabState';

/** The Studio's undo depth (kept from the pre-Yjs stack). */
export const FORMA_UNDO_LIMIT = 100;

/** Drop what is never part of the forma's content: the server's derived
 *  pending-publish flag and the transient wire flag. */
export function stripDerived(cfg: StandardConfig): StandardConfig {
  const rest = { ...(cfg as StandardConfig & { collabMirror?: boolean; syncBase?: unknown }) };
  delete rest.draftDirty;
  delete rest.collabMirror;
  // The forma valve's base (migration 063) is server business only.
  delete rest.syncBase;
  return rest;
}

/** How Publish writes: a live shared draft goes out as the flagged mirror
 *  write (the server gates it on formas.publish and keeps the draft); the
 *  first write of a brand-new forma (no draft yet) is an ordinary create. */
export function publishSaveOptions(live: boolean): { collabMirror: true } | undefined {
  return live ? { collabMirror: true } : undefined;
}

/** Legacy migration, as a READ-time normalisation: {EndOfSection} living in a
 *  post-body zone becomes the EOS toggle. Returns the SAME object when there
 *  is nothing to do. Never writes the document - the next real edit or the
 *  Publish carries it. */
export function normalizeEos(cur: StandardConfig): StandardConfig {
  const sp = cur.special as unknown as Record<string, string[] | undefined>;
  const keys = ['postLeft', 'postCenter', 'postRight'] as const;
  const isEos = (t: string) => t.replace(/\|.*$/, '') === '{EndOfSection}';
  if (!keys.some((k) => (sp?.[k] || []).some(isEos))) return cur;
  return {
    ...cur,
    special: {
      ...cur.special,
      postLeft: (sp.postLeft || []).filter((t) => !isEos(t)),
      postCenter: (sp.postCenter || []).filter((t) => !isEos(t)),
      postRight: (sp.postRight || []).filter((t) => !isEos(t)),
      eosEnabled: true,
    },
  };
}

/** One local edit: run the pure model function on the current forma, write
 *  ONLY what changed into the shared document (one transaction, tagged
 *  `origin` so the undo manager tracks it). Returns the new forma. */
export function applyLocalEdit(
  ydoc: Y.Doc,
  current: StandardConfig,
  fn: (f: StandardConfig) => StandardConfig,
  origin: unknown
): StandardConfig {
  const next = fn(current);
  if (next !== current) applyFormaDiff(ydoc, current, next, origin);
  return next;
}

/** PUBLISH: read the shared document, stamp the edit history, hand the result
 *  to `save` (which writes the published columns with the mirror flag), and
 *  only when the server accepted it: record the history in the document and
 *  clear the pending flag for everyone in ONE transaction. A rejected save
 *  (403 …) throws and leaves the document exactly as it was - still pending. */
export async function publishFromDoc(
  ydoc: Y.Doc,
  o: {
    userName: string;
    userId: string | undefined;
    /** false = the first write of a brand-new forma ('created'). */
    savedOnce: boolean;
    save: (cfg: StandardConfig) => Promise<StandardConfig>;
  }
): Promise<StandardConfig> {
  const raw = readForma(ydoc);
  const cur = normalizeEos(stripDerived(raw));
  const toSave: StandardConfig = {
    ...cur,
    editHistory: pushEditHistory(
      cur.editHistory,
      o.userName,
      o.userId,
      o.savedOnce ? 'edited' : 'created'
    ),
  };
  // Anyone's edit that lands while the save is in flight is NOT in what went
  // out: the draft stays pending for it (never "Published" over an edit).
  let editedDuring = false;
  const watch = (_u: Uint8Array, origin: unknown) => {
    if (origin !== 'publish') editedDuring = true;
  };
  ydoc.on('update', watch);
  let saved: StandardConfig;
  try {
    saved = await o.save(toSave);
  } finally {
    ydoc.off('update', watch);
  }
  ydoc.transact(() => {
    applyFormaDiff(ydoc, raw, toSave, 'publish');
    if (!editedDuring) markDirty(ydoc, false, 'publish');
  }, 'publish');
  return stripDerived(saved);
}

export type FormaChipState = 'published' | 'pending' | 'saving' | 'offline' | 'connecting';

/** The one chip: Offline wins (edits queue locally); then not-yet-synced is
 *  Connecting…; any write in flight (unsent Yjs updates or a Publish) is
 *  Saving…; else Pending publish / Published by the document's own flag. A
 *  not-yet-created forma (no shared document) is simply Pending publish. */
export function deriveFormaChip(input: {
  live: boolean;
  status: CollabStatus;
  ready: boolean;
  unsyncedChanges: number;
  publishing: boolean;
  dirty: boolean;
}): FormaChipState {
  if (!input.live) return input.publishing ? 'saving' : input.dirty ? 'pending' : 'published';
  if (input.status === 'offline') return 'offline';
  if (input.status === 'connecting' || !input.ready) return 'connecting';
  if (input.unsyncedChanges > 0 || input.publishing) return 'saving';
  return input.dirty ? 'pending' : 'published';
}

export function formaChipLabel(state: FormaChipState): string {
  switch (state) {
    case 'published':
      return 'Published';
    case 'pending':
      return 'Pending publish';
    case 'saving':
      return 'Saving…';
    case 'offline':
      return 'Offline';
    default:
      return 'Connecting…';
  }
}

/** How the Studio reacts when its shared document ends under it. 4410 (the
 *  row changed outside the editor - includes OUR OWN Discard) re-reads and
 *  keeps the mode; 4409 (ended elsewhere) re-reads and drops to View. */
export function endedPlan(reset: boolean) {
  return draftReloadPlan(reset ? 'reset' : 'ended-elsewhere');
}

/** A blocked socket (auth / origin / too many / denied) cannot be edited
 *  through: the Studio drops to View. */
export function dropsToView(blocked: LiveBlock | null): boolean {
  return blocked !== null;
}

function nameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Publish confirm body: names who else is in the draft. */
export function publishConfirmBody(peers: Array<{ name: string }>): string {
  if (!peers.length) return 'Publish this forma? Everyone will use it from now on.';
  const who = nameList(peers.map((p) => p.name));
  return `Publishes everyone's edits, including ${who}'s. The firm uses this forma from now on.`;
}

/** Discard-draft confirm body: names the other editors who lose their edits. */
export function discardDraftBody(peers: Array<{ name: string }>): string {
  if (!peers.length) {
    return 'Throws away the pending edits and goes back to the published forma. This cannot be undone.';
  }
  const who = nameList(peers.map((p) => p.name));
  return `Also discards ${who}'s pending edits. Everyone goes back to the published forma. This cannot be undone.`;
}

/** Awareness states -> each other person's last-touched keys, as
 *  `key -> 1-based cursor slot` (later people win a shared key). Only people
 *  who announced themselves (`user`) are shown; not me, not my other window. */
export function peerSelectionsFromAwareness(
  states: Iterable<[number, unknown]>,
  myClientId: number,
  myName: string
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [clientId, raw] of states) {
    if (clientId === myClientId) continue;
    const st = raw as {
      user?: { name?: unknown; slot?: unknown } | null;
      selection?: unknown;
    } | null;
    const name = typeof st?.user?.name === 'string' ? st.user.name.trim() : '';
    if (!name || name === myName) continue;
    const slot = st?.user?.slot;
    if (typeof slot !== 'number' || slot < 1 || slot > CURSOR_SLOTS) continue;
    if (!Array.isArray(st?.selection)) continue;
    for (const k of st.selection as unknown[]) if (typeof k === 'string') out[k] = slot;
  }
  return out;
}

/** The people chips plus their selections, from one awareness read. */
export function peerNames(peers: CollabPeer[]): Array<{ name: string }> {
  return peers.map((p) => ({ name: p.name }));
}

const KEY_RE = /^[A-Za-z0-9:_-]{1,80}$/;

/** The tint a peer's selection paints on the sheet: one rule per selected key
 *  (tokens by `data-token-key`, levels by `.dsg-level-N`, the End-of-Section
 *  line). Pure CSS from awareness - no DOM mutation, nothing to clean up. */
export function peerSelectionCss(peerSel: Record<string, number>): string {
  const rules: string[] = [];
  for (const [key, slot] of Object.entries(peerSel)) {
    if (!KEY_RE.test(key) || !(slot >= 1 && slot <= CURSOR_SLOTS)) continue;
    let target: string | null = null;
    if (key.startsWith('token:')) target = `.sfs-studio-canvas [data-token-key="${key}"]`;
    else if (/^level:[1-9]$/.test(key)) target = `.sfs-studio-body .dsg-level-${key.slice(6)}`;
    else if (key === 'eos') target = '.sfs-studio-canvas .dsg-eos';
    if (!target) continue;
    rules.push(
      `${target}{outline:thin solid var(--collab-cursor-${slot});background:color-mix(in srgb, var(--collab-cursor-${slot}) 14%, transparent);border-radius:var(--radius-sm)}`
    );
  }
  return rules.join('');
}

/** The Designer's project forma (appendix item 9): which row's shared
 *  document is open. A MASTER resolved for a project spec is copy-on-write —
 *  nothing opens until the first edit forks the project's copy (then the
 *  copy's document opens); a project copy (or a master outside any project)
 *  opens its own document straight away. */
export function projectFormaTarget(input: {
  base: Pick<StandardConfig, 'id' | 'projectId'> | null;
  hasProject: boolean;
  forked: Pick<StandardConfig, 'id'> | null;
}): { liveId: string | null; needsFork: boolean } {
  if (input.forked) return { liveId: input.forked.id, needsFork: false };
  if (!input.base) return { liveId: null, needsFork: false };
  if (!input.base.projectId && input.hasProject) return { liveId: null, needsFork: true };
  return { liveId: input.base.id, needsFork: false };
}

/** Which forma every road OUT (Export .dotx, Export to project) reads: the
 *  PUBLISHED row, never the shared draft. A never-published (unsaved) forma
 *  must publish first. */
export function pickExportSource<T>(input: {
  published: T | null;
  draftForma: T | null;
  unsaved: boolean;
}): { source: T | null; publishFirst: boolean } {
  if (input.unsaved) return { source: null, publishFirst: true };
  return { source: input.published ?? input.draftForma, publishFirst: false };
}

/* ─── Copy-on-write queue (Designer project forma) ───
   Edits made before the forked copy's shared document is open wait here,
   then replay into it. Nothing is ever dropped silently. */

export type FormaEdit = (f: StandardConfig) => StandardConfig;

export const FORMA_LOCKED_MESSAGE = 'Your forma edits could not be saved — the forma is locked';
export const FORMA_OPENING_MESSAGE =
  'Your forma edits are not saved yet — the project forma is still opening. Try again in a moment.';

/** The forked copy's document can take the queued edits: it is the open
 *  document, it has synced with the server and it holds the forma. */
export function queueReady(input: {
  forkedId: string | null;
  liveId: string | null;
  synced: boolean;
  seeded: boolean;
}): boolean {
  return !!input.forkedId && input.liveId === input.forkedId && input.synced && input.seeded;
}

/** Replay the queue in order through `apply` (false = refused: the document
 *  is blocked / read-only). A refusal stops the replay and KEEPS that edit
 *  and everything after it, so a retry continues where it stopped. */
export function replayQueuedEdits(
  queue: FormaEdit[],
  apply: (fn: FormaEdit) => boolean
): { remaining: FormaEdit[]; applied: number; blocked: boolean } {
  for (let i = 0; i < queue.length; i++) {
    if (!apply(queue[i])) return { remaining: queue.slice(i), applied: i, blocked: true };
  }
  return { remaining: [], applied: queue.length, blocked: false };
}

/** Poll `done` until true or `timeoutMs` passes. Resolves true / false. */
export async function waitUntil(
  done: () => boolean,
  timeoutMs: number,
  intervalMs = 50
): Promise<boolean> {
  const until = Date.now() + timeoutMs;
  while (!done()) {
    if (Date.now() >= until) return false;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return true;
}
